import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import difference from 'lodash/difference';
import { FieldArray } from 'formik';
import { Button, Alert, Icon } from '@storable/canaveral-components';
import { object, string, array } from 'yup';
import useCurrentUser from '../../hooks/useCurrentUser';
import { MANAGE } from '../../../modules/authorization/actions';
import { FACILITY_PAGES, NON_FACILITY_PAGES } from '../../../modules/authorization/subjects';
import MediaGalleryRightPanel from '../MediaGalleryRightPanel';
import { ALL } from '../MediaGallery/constants';
import formatInitialValues from '../../lib/formatInitialValues';
import withForm from '../../hocs/withForm';
import FormSubmitButton from '../FormSubmitButton';
import FormFieldText from '../FormFieldText';
import FormFieldFacilitySelect from '../FormFieldFacilitySelect';
import { DEFAULT_LOCALE, LOCALE_LANGUAGE_NAME_MAPPING } from '../../../modules/i18n/locales';
import styles from './styles.scss';

const propTypes = {
  medium: PropTypes.shape({
    filename: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    defaultMetadata: PropTypes.shape({
      locale: PropTypes.oneOf([DEFAULT_LOCALE]),
      altText: PropTypes.string,
      title: PropTypes.string,
    }),
    metadata: PropTypes.arrayOf(PropTypes.shape({
      locale: PropTypes.string,
      altText: PropTypes.string,
      title: PropTypes.string,
    })),
    identifier: PropTypes.string,
    facilityId: PropTypes.string,
  }).isRequired,
  i18nEnabled: PropTypes.bool.isRequired,
  filter: PropTypes.string.isRequired,
  updateFilter: PropTypes.func.isRequired,
  banners: PropTypes.shape({
    success: PropTypes.string,
    errors: PropTypes.arrayOf(PropTypes.string),
    info: PropTypes.string,
  }).isRequired,
  setBanners: PropTypes.func.isRequired,
  selectedMedium: PropTypes.string.isRequired,
  setSelectedMedium: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  errors: PropTypes.shape(),
  dirty: PropTypes.bool.isRequired,
  submitting: PropTypes.bool,
  updateData: PropTypes.shape({
    userErrors: PropTypes.array.isRequired,
  }),
  values: PropTypes.arrayOf(PropTypes.shape({
    defaultMetadata: PropTypes.shape({
      locale: PropTypes.oneOf([DEFAULT_LOCALE]),
      altText: PropTypes.string,
      title: PropTypes.string,
    }),
    metadata: PropTypes.arrayOf(PropTypes.shape({
      locale: PropTypes.string,
      altText: PropTypes.string,
      title: PropTypes.string,
    })),
    identifier: PropTypes.string,
    facilityId: PropTypes.string,
  })).isRequired,
};

const defaultProps = {
  submitting: undefined,
  errors: {},
  updateData: undefined,
};

const initialValues = {
  defaultMetadata: {
    locale: DEFAULT_LOCALE,
    altText: '',
    title: '',
  },
  metadata: [],
  identifier: undefined,
  facilityId: undefined,
};

const validationSchema = object().shape({
  defaultMetadata: object().shape({
    locale: string(),
    altText: string().required('Alt text is required'),
    title: string(),
  }),
  metadata: array()
    .of(
      object().shape({
        locale: string(),
        altText: string().nullable(),
        title: string().nullable(),
      }),
    ),
  identifier: string(),
  facilityId: string().nullable(),
});

const MediaGalleryEdit = ({
  dirty,
  errors: formErrors,
  medium,
  i18nEnabled,
  filter,
  updateFilter,
  selectedMedium,
  banners,
  setBanners,
  setSelectedMedium,
  handleSubmit,
  submitting,
  updateData,
  values,
}) => {
  useEffect(() => {
    if (updateData) {
      if (updateData.mediumUpdate.userErrors.length) {
        setBanners({ errors: ['There was a problem updating your image.  Please try again.'] });
      } else {
        updateFilter(ALL);
        setSelectedMedium(undefined);
        setBanners({ success: 'Success!  Your changes have been saved.' });
      }
    }
  }, [updateData]);

  const currentUser = useCurrentUser();
  const unauthorized = currentUser.cannot(MANAGE, FACILITY_PAGES, NON_FACILITY_PAGES);

  const onClickCancel = () => {
    updateFilter(ALL);
    setBanners({});
  };

  const defaultMetadataFields = (
    <div className={styles.input}>
      {i18nEnabled && (
        <>
          <div className={styles.locale}>
            <span>{LOCALE_LANGUAGE_NAME_MAPPING[DEFAULT_LOCALE]}</span>
            <span className={styles.defaultLocale}>default</span>
          </div>
          <hr />
        </>
      )}
      <FormFieldText
        label="Title Text"
        name="defaultMetadata.title"
        disabled={unauthorized}
      />
      <FormFieldText
        data-testid="media-gallery-default-alt-text-input"
        label="Alt Text"
        name="defaultMetadata.altText"
        disabled={unauthorized}
        required
      />
      <FormFieldFacilitySelect
        name="facilityId"
        label="Facility"
        showDisabled
        tooltip="If this image is associated with a facility, you can add it
          here to help you locate the image and add it to widgets later."
        disabled={unauthorized}
        style={{ borderColor: '#d8d8d8' }}
      />
      <FormFieldText
        label="File Identifier"
        name="identifier"
        tooltip="Use this field to help your identify and find this image later."
        disabled={unauthorized}
      />
    </div>
  );

  const i18nMetadataFields = (
    <FieldArray name="metadata">
      <>
        {values.metadata.map(({ locale }, idx) => (
          <div className={styles.input} key={locale}>
            <div className={styles.locale}>
              {LOCALE_LANGUAGE_NAME_MAPPING[locale]}
            </div>
            <hr />
            <FormFieldText
              label="Title Text"
              name={`metadata.${idx}.title`}
            />
            <FormFieldText
              data-testid="media-gallery-i18n-alt-text-input"
              label="Alt Text"
              name={`metadata.${idx}.altText`}
            />
          </div>
        ))}
      </>
    </FieldArray>
  );

  return (
    <>
      <form onSubmit={handleSubmit} onReset={() => {}}>
        <div className={styles.container}>
          <div className={styles.mainPane}>
            {banners.success && (
              <Alert
                className={styles.alert}
                context="success"
              >
                <Icon className={styles.icon} id="success" context="success" label="success" />
                <span className={styles.alertText}>{banners.success}</span>
              </Alert>
            )}
            {banners.errors && (
              banners.errors.map(err => (
                <Alert
                  className={styles.alert}
                  context="danger"
                  key={err}
                >
                  <Icon className={styles.icon} id="warning" context="danger" label="warning" />
                  <div className={styles.alertText}>{err}</div>
                </Alert>
              ))
            )}
            {banners.info && (
              <Alert
                className={styles.alert}
                context="primary"
              >
                <Icon className={styles.icon} id="info" context="primary" label="info" />
                <div className={styles.alertText}>{banners.info}</div>
              </Alert>
            )}
            <img src={medium.url} alt="preview" className={styles.preview} />
            <div className={styles.inputs}>
              <div className={styles.filename}>
                {medium.filename}
              </div>
              {defaultMetadataFields}
              {i18nEnabled && i18nMetadataFields}
            </div>
          </div>
          <div className={styles.rightPane}>
            <MediaGalleryRightPanel
              selectedMedium={selectedMedium}
              filter={filter}
              updateFilter={updateFilter}
              setSelectedMedium={setSelectedMedium}
              setBanners={setBanners}
            />
          </div>
        </div>
        <div className={styles.footer}>
          <Button
            className={styles.footerButton}
            onClick={onClickCancel}
            context="neutral"
          >
            Cancel
          </Button>
          <FormSubmitButton
            data-testid="media-gallery-edit-form-submit"
            className={styles.footerButton}
            innerText={submitting ? 'Updating Image' : 'Update Image'}
            disabled={Object.keys(formErrors).length > 0 || !dirty || unauthorized}
            submitting={submitting}
          />
        </div>
      </form>
    </>
  );
};

MediaGalleryEdit.propTypes = propTypes;
MediaGalleryEdit.defaultProps = defaultProps;

MediaGalleryEdit.fragments = {
  medium: gql`
    fragment MediaGalleryEditMedium on Medium {
      id
      url
      filename
      identifier
      facilityId
      defaultMetadata {
        locale
        altText
        title
      }
      metadata(locales: $locales) {
        id
        locale
        altText
        title
      }
    }
  `,
};

export default withForm({
  enableReinitialize: true,
  mapPropsToValues: (props) => {
    const { medium, locales, i18nEnabled } = props;

    // i18n not enabled, do not format medium metadata
    if (!i18nEnabled) {
      return formatInitialValues(initialValues, medium);
    }

    // no metadata, set default metadata for form values
    if (medium && (!medium.metadata || !medium.metadata.length)) {
      const metadata = locales.map(locale => ({
        locale,
      }));

      return formatInitialValues(
        initialValues,
        {
          ...medium,
          metadata,
        },
      );
    }

    // metadata is missing a locale, add locale to metadata for form values
    if (medium && (medium.metadata.length !== locales.length)) {
      const { metadata: mediumMetadata } = medium;
      const missingLocales = difference(locales, mediumMetadata.map(({ locale }) => locale));
      const defaultLocaleMetadata = missingLocales.map(locale => ({
        locale,
      }));

      const metadata = [
        ...mediumMetadata,
        ...defaultLocaleMetadata,
      ];

      return formatInitialValues(
        initialValues,
        {
          ...medium,
          metadata,
        },
      );
    }

    return formatInitialValues(initialValues, props.medium);
  },
  validationSchema,
  handleSubmit: (input, { props }) => props.onUpdateMedium(input),
})(MediaGalleryEdit);
